import { firestore } from "firebase";

import { DryingProjectOverview } from "@/data/models/DryingProjectOverview";
import LeakDetectionProjectOverview from "@/data/models/LeakDetectionProjectOverview";
import { ProjectSortOrder } from "@/data/models/ProjectSortOrder";

/**
 * Project overviews as handled by the project lists: drying and leak detection
 * overviews are mixed, and sorting only uses fields both of them share.
 */
type SortableProjectOverview =
  | DryingProjectOverview
  | LeakDetectionProjectOverview;

/**
 * One collator shared by all project list sorting.
 *
 * `String.prototype.localeCompare` builds a collator on every call, which
 * dominates the run time when sorting the archive (>10k projects means ~10^5
 * comparisons). A cached `Intl.Collator` yields the same ordering as the
 * default `localeCompare` at a fraction of the cost.
 */
const projectCollator = new Intl.Collator();

/**
 * Locale-aware string comparison for project list sorting.
 * Equivalent to `first.localeCompare(second)`, but without rebuilding the
 * collator for every comparison.
 */
export function compareProjectStrings(first: string, second: string) {
  return projectCollator.compare(first, second);
}

export interface ArchivedProjectSortOptions {
  sortOrder: ProjectSortOrder;
  /** The deleted-projects view sorts by deletion date instead of start date. */
  isDeletedProjectsList: boolean;
  /**
   * SanTec sorts by the "Auftrag angenommen" timestamp whenever that predates
   * the stored start date.
   */
  isSanTecClient: boolean;
}

/**
 * The date an archived project is sorted by: the deletion date in the
 * deleted-projects view, otherwise the project start date, which SanTec
 * replaces by the "Auftrag angenommen" timestamp when that is earlier.
 *
 * Returns `undefined`/`null` for projects that carry none of these dates.
 */
export function getArchivedProjectSortDate(
  projectOverview: SortableProjectOverview,
  options: Pick<
    ArchivedProjectSortOptions,
    "isDeletedProjectsList" | "isSanTecClient"
  >
): firestore.Timestamp | null | undefined {
  if (options.isDeletedProjectsList) {
    return (
      projectOverview.conclusion?.conclusionDate ??
      projectOverview.lastOperationAt ??
      projectOverview.endDate
    );
  }

  const startDate = projectOverview.startDate;

  if (options.isSanTecClient) {
    const projectAcceptedAt = (projectOverview as DryingProjectOverview)
      .dryingProjectSteps?.projectAccepted?.creationTimestamp;

    if (projectAcceptedAt != null) {
      const projectAcceptedDate = firestore.Timestamp.fromDate(
        new Date(projectAcceptedAt)
      );

      if (projectAcceptedDate.toMillis() < startDate?.toMillis()) {
        return projectAcceptedDate;
      }
    }
  }

  return startDate;
}

/**
 * Descending by IRES project number. Used by the archive list's project number
 * sort order and by the search across all projects.
 */
export function sortProjectsByIresProjectNumber<
  T extends SortableProjectOverview
>(projectOverviews: T[]): T[] {
  return [...projectOverviews].sort((first, second) =>
    compareProjectStrings(
      second.projectNumberIres ?? "",
      first.projectNumberIres ?? ""
    )
  );
}

/**
 * Sorts archived project overviews for the "Alte Projekte" list: newest or
 * oldest first by sort date, or descending by IRES project number. Ties fall
 * back to the project name, then to the project number descending.
 *
 * Projects without a sort date are ordered ahead of dated ones, in both
 * directions.
 *
 * The sort date is resolved once per project instead of inside the comparator,
 * which runs O(n log n) times: building Firestore timestamps there was the
 * dominant cost for large archives.
 */
export function sortArchivedProjects<T extends SortableProjectOverview>(
  projectOverviews: T[],
  options: ArchivedProjectSortOptions
): T[] {
  if (options.sortOrder === ProjectSortOrder.PROJECT_NUMBER) {
    return sortProjectsByIresProjectNumber(projectOverviews);
  }

  const decorated = projectOverviews.map((projectOverview) => ({
    projectOverview,
    sortDate: getArchivedProjectSortDate(projectOverview, options),
  }));

  decorated.sort((first, second) => {
    const firstDate = first.sortDate;
    const secondDate = second.sortDate;

    if (!secondDate) {
      return 1;
    } else if (!firstDate) {
      return -1;
    }

    let difference = secondDate.seconds - firstDate.seconds;

    if (difference === 0) {
      difference = compareProjectStrings(
        first.projectOverview.projectName ?? "",
        second.projectOverview.projectName ?? ""
      );
    }

    if (difference === 0) {
      difference = compareProjectStrings(
        second.projectOverview.projectNumber ?? "",
        first.projectOverview.projectNumber ?? ""
      );
    }

    return options.sortOrder === ProjectSortOrder.OLDEST_FIRST
      ? -difference
      : difference;
  });

  return decorated.map((entry) => entry.projectOverview);
}
